import type { Recipe } from '@/types/recipe'
import type { ShoppingListItem } from '@/types/shopping-list'
import { ingredientParser } from './parser'
import IngredientScaler from './scaler'
import type { MealPlanShoppingListData } from './aggregator'
import { getCategoryDisplayName } from './categories'

// Re-export ShoppingListItem type
export type { ShoppingListItem }

export interface ShoppingListExportOptions {
  format: 'text' | 'csv' | 'json'
  servings?: number // Scale recipe for different serving sizes
  includeTitle?: boolean
  includeInstructions?: boolean
}

export interface ShoppingListExportResult {
  success: boolean
  data?: string | object
  error?: string
  items?: ShoppingListItem[]
}


export class ShoppingListService {
  convertRecipeToShoppingList(recipe: Recipe, options: Partial<ShoppingListExportOptions> = {}): ShoppingListItem[] {
    if (!recipe.ingredients || recipe.ingredients.length === 0) {
      return []
    }

    let ingredients = [...recipe.ingredients]

    if (options.servings && recipe.servings && options.servings !== recipe.servings) {
      ingredients = IngredientScaler.scaleIngredients(ingredients, recipe.servings, options.servings)
    }

    return ingredientParser.parseIngredientsToShoppingList(ingredients)
  }

  async exportToFormat(recipe: Recipe, options: ShoppingListExportOptions): Promise<ShoppingListExportResult> {
    try {
      const items = this.convertRecipeToShoppingList(recipe, options)

      switch (options.format) {
        case 'text':
          return this.exportToText(recipe, items, options)
        case 'csv':
          return this.exportToCSV(recipe, items, options)
        case 'json':
          return this.exportToJSON(recipe, items, options)
        default:
          return {
            success: false,
            error: `Unsupported export format: ${options.format}`
          }
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      }
    }
  }

  exportToText(recipe: Recipe, items: ShoppingListItem[], options: ShoppingListExportOptions): ShoppingListExportResult {
    let text = ''
    
    if (options.includeTitle) {
      text += `Boodschappenlijst voor: ${recipe.title}\n`
      if (options.servings && recipe.servings) {
        text += `Aangepast voor ${options.servings} ${options.servings === 1 ? 'portie' : 'porties'}\n`
      }
      text += '\n'
    }
    
    text += 'Ingrediënten:\n'
    text += items.map(item => `- ${ingredientParser.formatShoppingListItem(item)}`).join('\n')
    
    if (options.includeInstructions && recipe.instructions) {
      text += '\n\nBereidingswijze:\n'
      text += recipe.instructions.map((instruction, index) => `${index + 1}. ${instruction}`).join('\n')
    }

    return {
      success: true,
      data: text,
      items
    }
  }

  exportToCSV(recipe: Recipe, items: ShoppingListItem[], _options: ShoppingListExportOptions): ShoppingListExportResult {
    const headers = ['Item', 'Hoeveelheid', 'Eenheid', 'Specificatie', 'Recept']
    const rows = items.map(item => [
      item.name,
      item.quantity?.toString() || '',
      item.unit || '',
      item.specification || '',
      recipe.title
    ])

    const csv = [headers, ...rows]
      .map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
      .join('\n')

    return {
      success: true,
      data: csv,
      items
    }
  }

  exportToJSON(recipe: Recipe, items: ShoppingListItem[], options: ShoppingListExportOptions): ShoppingListExportResult {
    const data = {
      recipe: {
        title: recipe.title,
        servings: options.servings || recipe.servings
      },
      exportDate: new Date().toISOString(),
      items: items.map(item => ({
        name: item.name,
        quantity: item.quantity,
        unit: item.unit,
        specification: item.specification,
        formatted: ingredientParser.formatShoppingListItem(item)
      }))
    }

    return {
      success: true,
      data: JSON.stringify(data, null, 2),
      items
    }
  }

  // ===== UTILITY FUNCTIONS =====

  downloadAsFile(content: string, filename: string, mimeType: string = 'text/plain') {
    const blob = new Blob([content], { type: mimeType })
    const url = URL.createObjectURL(blob)
    
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    URL.revokeObjectURL(url)
  }

  copyToClipboard(text: string): Promise<boolean> {
    return navigator.clipboard.writeText(text)
      .then(() => true)
      .catch(() => false)
  }
}

export const exportMealPlanShoppingList = (
  shoppingData: MealPlanShoppingListData,
  format: 'text' | 'csv' | 'json'
): string => {
  switch (format) {
    case 'text':
      return exportMealPlanToText(shoppingData)
    case 'csv':
      return exportMealPlanToCSV(shoppingData)
    case 'json':
      return JSON.stringify(shoppingData, null, 2)
    default:
      throw new Error(`Unsupported format: ${format}`)
  }
}

const exportMealPlanToText = (data: MealPlanShoppingListData) => {
  let text = 'Boodschappenlijst voor Weekplanning\n'
  text += `Periode: ${data.date_range.start_date} tot ${data.date_range.end_date}\n`
  text += `${data.total_meals} maaltijden, ${data.total_recipes} recepten\n\n`

  for (const category of data.categories) {
    text += `${getCategoryDisplayName(category.category)}:\n`
    for (const item of category.items) {
      text += `- ${ingredientParser.formatShoppingListItem(item)}`
      if (item.recipe_titles.length > 1) {
        text += ` (${item.recipe_titles.join(', ')})`
      }
      text += '\n'
    }
    text += '\n'
  }

  return text
}

const exportMealPlanToCSV = (data: MealPlanShoppingListData) => {
  const headers = ['Categorie', 'Item', 'Hoeveelheid', 'Eenheid', 'Specificatie', 'Recepten', 'Gekocht']
  const rows = data.all_items.map((item) => [
    getCategoryDisplayName(item.category),
    item.name,
    item.quantity?.toString() || '',
    item.unit || '',
    item.specification || '',
    item.recipe_titles.join('; '),
    item.is_purchased ? 'Ja' : 'Nee',
  ])

  return [headers, ...rows]
    .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(','))
    .join('\n')
}


// Export singleton instance
export const shoppingListService = new ShoppingListService()
